import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const BOTTOM_THRESHOLD_PX = 48;

export function useCampaignThreadMessageScroll(messageCount: number, lastMessageKey?: string) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const stickToBottomRef = useRef(true);
    const prevLastMessageKeyRef = useRef<string | undefined>(undefined);
    const [pendingNewCount, setPendingNewCount] = useState(0);

    const isNearBottom = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX;
    }, []);

    const clearPending = useCallback(() => {
        setPendingNewCount(0);
    }, []);

    const scrollToLatest = useCallback(
        (behavior: ScrollBehavior = 'smooth') => {
            const el = scrollRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior });
            stickToBottomRef.current = true;
            clearPending();
        },
        [clearPending],
    );

    const handleScroll = useCallback(() => {
        const nearBottom = isNearBottom();
        stickToBottomRef.current = nearBottom;
        if (nearBottom) {
            clearPending();
        }
    }, [clearPending, isNearBottom]);

    useLayoutEffect(() => {
        if (messageCount === 0) {
            prevLastMessageKeyRef.current = undefined;
            clearPending();
            return;
        }

        if (prevLastMessageKeyRef.current == null) {
            scrollToLatest('auto');
            prevLastMessageKeyRef.current = lastMessageKey;
            return;
        }

        if (lastMessageKey === prevLastMessageKeyRef.current) return;

        if (stickToBottomRef.current || isNearBottom()) {
            scrollToLatest('smooth');
        } else {
            setPendingNewCount(count => count + 1);
        }

        prevLastMessageKeyRef.current = lastMessageKey;
    }, [clearPending, isNearBottom, lastMessageKey, messageCount, scrollToLatest]);

    return {
        scrollRef,
        pendingNewCount,
        scrollToLatest,
        handleScroll,
    };
}
